import { graphql, useStaticQuery } from 'gatsby'

import { GlobalSettingsQuery } from '../graphql/gatsby'

export type GlobalSettings = NonNullable<GlobalSettingsQuery>
export type UseGlobalSettings = GlobalSettings

export const useGlobalSettings = (): UseGlobalSettings => {
  return useStaticQuery<GlobalSettingsQuery>(graphql`
    query globalSettings {
      sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
        title
        lang
        description
        keywords
        _rawDefaultOpenGraphImage(resolveReferences: { maxDepth: 5 })
        ...eftForm
        ...taxIdForm
        ...commissionsOptOutForm
        ...siteLogo
        ...defaultHeroTag
        ...frontPage
        ...acceptedCreditCards
        ...freeShippingThresholds
        ...shippingTooltips
        ...mainMenuFragment
        ...utilityMenuFragment
        ...brandMenuFragment
        ...mobileMenuFragment
        ...notificationMessageFragment
        ...footerCompanyMenuFragment
        ...footerHelpMenuFragment
        ...footerLegalFootnotesFragment
        ...footerLegalMenuFragment
        ...footerQuestionMenuFragment
        ...footerResourcesMenuFragment
        ...footerSocialMenuFragment
        ...helpCenterMenuFragment
        ...noSearchResultsFound
        ...returnPolicyText
        ...returnsHelpText
        ...dispensaryUpdateCartErrorMessage
        ...datasheetDisclaimerFragment
        ...drugNutrientRequestBanner
        ...dispensaryBannerTextDisclaimer
        ...blockedUserErrorMessage
        ...onboardingPatientSpotlight
        ...top25Drugs
      }
      site {
        siteMetadata {
          title
          description
          author
          buildTime
          commissionsNotificationDaysToHide
          commissionsNotificationUseModal
          commissionsNotificationToastOpensModal
          featureFlags {
            allowIncompleteOnboardingCheckout
            anonymousCart
            anonymousSupportRequest
            applePay
            autoShipAndSave
            autoShipConfirmation
            commissionsCashBack
            commissionsOptOut
            disableInvalidDispensaries
            expressCheckout
            googlePay
            manageDispensary
            manageDispensingOptions
            manageEScriptBuilder
            manageInboundPatientReferral
            manageCommissionsPayouts
            manageRebateProgram
            manageWellWorld
            manageGenomicsSpotlight
            practitionerDropShip
            practitionerFormsReminder
            practitionerHub
            practitionerHubLinks
            productLabelStatus
            prop65Warnings
            rewardsProgram
            showN1PractitionerBrand
            showRetailPrices
            smsOptIn
            subscribeAndSaveRedesign
            supportRequest
            truemed
            onboardCustomer
            onboardPractitioner
            onboardStudent
            disabledRegistration
          }
          globalPromotions
          googleAdsEventsByCategory {
            patient
            practitioner
            employee
            other_professional
          }
          legalAgreements
          managePrivacyLabel
          manageWellWorldEntryPoint
          manageGenomicsSpotlightEntryPoint
          onboardingMarket
          practitionerDocuments
          productionSiteBaseUrl
          classicSiteUrl
          registerTypeDefault
          rewardsTenant
          shippingRestrictions
          testKitProducts {
            sku
            processingSku
          }
          onboardPractitioner {
            displayPrimaryBrand
            displayPracticeType
            displayReferralCode
          }
        }
      }
      # Singleton content shared by every product page. It lives in this static query rather than the
      # product page query so the copy ships once instead of in every product's page data.
      sanityProductDetailPage {
        _rawProductResources(resolveReferences: { maxDepth: 10 })
        ...pdpTopBanner
        ...truemedBanner
      }
      sanitySearchModalContent {
        topSearches {
          title
          category
          image {
            asset {
              _id
              _type
              url
            }
          }
        }
        products {
          sku
        }
      }
      allSanityShippingCountry {
        nodes {
          country
          isShippingRestricted
          shippingRegions {
            ... on SanityShippingRegionAu {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionCa {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionCh {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionCn {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionGb {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionNz {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionSg {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
            ... on SanityShippingRegionUs {
              isProductsRestrictedForAllRegions
              region
              restrictedProducts {
                sku
              }
            }
          }
        }
      }
    }
  `)
}
